import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, IconButton, InputAdornment, LinearProgress, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
  alpha, useTheme,
} from '@mui/material';
import { Add, Clear, ContactPhoneOutlined, FilterAltOff, Search } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { admissionEnquiryService } from '../../services/admissions/admissionEnquiryService';
import { classesApi } from '../../services/sessions/classesApi';
import { AdmissionEnquiry, AdmissionEnquiryStatus, AdmissionEnquiriesListResponse } from '../../types/admissions';
import { statusLabel } from '../../components/common/StatusChip';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import SearchableSelect, { SelectOption } from '../../components/common/SearchableSelect';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import EnquiryDialog from '../../components/admissions/enquiries/EnquiryDialog';
import EnquiryRow from '../../components/admissions/enquiries/EnquiryRow';
import EnquiryStatusMenu from '../../components/admissions/enquiries/EnquiryStatusMenu';
import { ENQUIRY_STATUSES } from '../../components/admissions/enquiries/enquiryStatuses';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { upcomingYears, yearLabel } from '../../utils/academicYear';
import { fmtClassName, usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { writeSheet, downloadXlsxBlob } from '../../utils/xlsx';
import ExportMenuButton, { type ExportKind } from '../../components/common/ExportMenuButton';
import { EnquiriesListDocument } from '../../components/admissions/pdf';
import { ENQUIRY_EXPORT_COLUMNS } from '../../components/admissions/enquiries/enquiryExport';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
// Fixed widths, so the table doesn't shift as search narrows it; the note takes what's left.
const COLUMNS: Array<{ label: string; width?: number | string }> = [
  { label: 'Child', width: '18%' }, { label: 'Admission for', width: 130 }, { label: 'Parent', width: '18%' },
  { label: 'Note' }, { label: 'Status', width: 150 }, { label: 'Added', width: 120 }, { label: '', width: 96 },
];

/**
 * Enquiries — families interested in admission before it opens, often a
 * year ahead. When admissions for a year open, the office filters by year
 * and class, calls each family, and marks how it went. Filters live in the
 * URL so a call list can be shared.
 */
const AdmissionEnquiriesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite: canWriteModule } = useModulePermissions();
  const canWrite = canWriteModule('admissions');

  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const search = useDebounce(searchInput, 300).trim();
  const [forYear, setForYear] = useState(searchParams.get('year') ?? '');
  const [classId, setClassId] = useState(searchParams.get('class') ?? '');
  const [status, setStatus] = useState<AdmissionEnquiryStatus | ''>((searchParams.get('status') as AdmissionEnquiryStatus) ?? '');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [data, setData] = useState<AdmissionEnquiriesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [classOptions, setClassOptions] = useState<SelectOption[]>([]);

  const [editing, setEditing] = useState<AdmissionEnquiry | null | undefined>(undefined); // undefined = closed, null = new
  const [removing, setRemoving] = useState<AdmissionEnquiry | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [statusMenu, setStatusMenu] = useState<{ anchor: HTMLElement; enquiry: AdmissionEnquiry } | null>(null);

  useEffect(() => {
    classesApi.list()
      .then(cs => setClassOptions(cs.filter(c => c.isActive).map(c => ({ value: c._id, label: fmtClassName(c.name) }))))
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load classes', { variant: 'error' }));
  }, [enqueueSnackbar]);

  // Filters → URL, so a call list ("2027–28 · Class 1 · New") can be shared.
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set('q', search);
    if (forYear) next.set('year', forYear);
    if (classId) next.set('class', classId);
    if (status) next.set('status', status);
    setSearchParams(next, { replace: true });
  }, [search, forYear, classId, status, setSearchParams]);

  useEffect(() => { setPage(0); }, [search, forYear, classId, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await admissionEnquiryService.list({
        q: search || undefined,
        forYear: forYear ? Number(forYear) : undefined,
        classId: classId || undefined,
        status: status || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      // The last row of a later page moved out of view (status change, removal): step back.
      if (!res.items.length && page > 0) { setPage(p => p - 1); return; }
      setData(res);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not load enquiries', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, forYear, classId, status, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const yearOptions = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'All years' }, ...upcomingYears().map(y => ({ value: String(y), label: yearLabel(y) }))],
    [],
  );
  const classFilterOptions = useMemo(() => [{ value: '', label: 'All classes' }, ...classOptions], [classOptions]);

  const counts = data?.counts;
  const total = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  const narrowed = !!(search || forYear || classId);
  const filtersActive = narrowed || !!status;
  const resetFilters = () => { setSearchInput(''); setForYear(''); setClassId(''); setStatus(''); };

  const onEdit = useCallback((e: AdmissionEnquiry) => setEditing(e), []);
  const onRemove = useCallback((e: AdmissionEnquiry) => setRemoving(e), []);
  const onStatusClick = useCallback((anchor: HTMLElement, enquiry: AdmissionEnquiry) => setStatusMenu({ anchor, enquiry }), []);

  const pickStatus = async (next: AdmissionEnquiryStatus) => {
    const target = statusMenu?.enquiry;
    setStatusMenu(null);
    if (!target || target.status === next) return;
    try {
      await admissionEnquiryService.update(target._id, { status: next });
      enqueueSnackbar(`${target.childName}: ${statusLabel({ kind: 'enquiry', value: next })}`, { variant: 'success' });
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not change the status', { variant: 'error' });
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await admissionEnquiryService.remove(removing._id);
      enqueueSnackbar(`${removing.childName} removed from enquiries`, { variant: 'success' });
      setRemoving(null);
      load();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not remove the enquiry', { variant: 'error' });
    } finally {
      setRemoveBusy(false);
    }
  };

  // ── Export — every enquiry the filters match, not just this page ──────
  const { branding } = useSchoolBranding();
  const { generate: generatePdf } = usePdfExport();
  const exportAs = async (kind: ExportKind) => {
    const filters = { q: search || undefined, forYear: forYear ? Number(forYear) : undefined, classId: classId || undefined, status: status || undefined };
    // "2027–28 · Class 1 · New · “ali”" — what the file holds.
    const summary = [
      forYear ? yearLabel(Number(forYear)) : '',
      classId ? classOptions.find(c => c.value === classId)?.label ?? '' : '',
      status ? statusLabel({ kind: 'enquiry', value: status }) : '',
      search ? `“${search}”` : '',
    ].filter(Boolean).join('  ·  ');
    const fileBase = ['enquiries', summary.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()].filter(Boolean).join('-');
    try {
      if (kind === 'xlsx') {
        const enquiries = await admissionEnquiryService.listAll(filters);
        const blob = await writeSheet({
          sheetName: 'Enquiries',
          columns: ENQUIRY_EXPORT_COLUMNS.map(c => ({ header: c.header, width: c.width })),
          rows: enquiries.map(e => ENQUIRY_EXPORT_COLUMNS.map(c => c.value(e))),
          emptyRowsForValidation: 0,
        });
        downloadXlsxBlob(`${fileBase}.xlsx`, blob);
      } else {
        await generatePdf(async () => (
          <EnquiriesListDocument
            enquiries={await admissionEnquiryService.listAll(filters)}
            branding={branding}
            meta={{ title: 'Enquiries', subtitle: summary || undefined, generatedAt: new Date().toISOString() }}
          />
        ), `${fileBase}.pdf`);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not export the enquiries', { variant: 'error' });
    }
  };

  const items = data?.items ?? [];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Enquiries</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Families interested in admission, often a year ahead. When admissions open, pick the year and class and call them.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ExportMenuButton size="medium" count={data?.pagination.totalItems ?? 0} noun="enquiries" filtered={filtersActive} onExport={exportAs} />
          {canWrite && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setEditing(null)} disableElevation>Add enquiry</Button>
          )}
        </Stack>
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <TextField
            size="small"
            placeholder="Search by child, parent or phone…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search"><Clear fontSize="small" /></IconButton></InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1, minWidth: 240 }}
          />
          <SearchableSelect label="Year" value={forYear} onChange={setForYear} options={yearOptions} placeholder="All years" minWidth={160} />
          <SearchableSelect label="Class" value={classId} onChange={setClassId} options={classFilterOptions} placeholder="All classes" minWidth={180} />
          {filtersActive && (
            <Button size="small" variant="text" onClick={resetFilters} startIcon={<FilterAltOff fontSize="small" />}>Reset</Button>
          )}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
          <ToggleButtonGroup
            size="small" exclusive value={status}
            onChange={(_, v) => v !== null && setStatus(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 500, whiteSpace: 'nowrap' } }}
          >
            {[{ value: '' as const, label: 'All', n: total }, ...ENQUIRY_STATUSES.map(s => ({ value: s.value, label: statusLabel({ kind: 'enquiry', value: s.value }), n: counts?.[s.value] ?? 0 }))].map(o => (
              <ToggleButton key={o.value} value={o.value}>
                {o.label}
                {counts && <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{o.n}</Box>}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        {loading && items.length > 0 && <LinearProgress sx={{ height: 2 }} />}
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed', minWidth: 960 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
                {COLUMNS.map((c, i) => (
                  <TableCell key={i} align={i === COLUMNS.length - 1 ? 'right' : 'left'} sx={{ width: c.width, fontWeight: 600, color: 'text.secondary' }}>{c.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRowSkeletons columns={COLUMNS.length} rows={5} />
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ py: 6 }}>
                    <EmptyState
                      icon={filtersActive ? <Search /> : <ContactPhoneOutlined />}
                      title={filtersActive ? 'No enquiries match' : 'No enquiries yet'}
                      description={filtersActive
                        ? 'Try another year, class or status, or reset the filters.'
                        : 'When a parent asks about admission before it opens, add them here so you can call them when it does.'}
                      action={filtersActive
                        ? { label: 'Reset filters', onClick: resetFilters }
                        : canWrite ? { label: 'Add enquiry', onClick: () => setEditing(null) } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : items.map(e => (
                <EnquiryRow key={e._id} enquiry={e} canWrite={canWrite} onEdit={onEdit} onRemove={onRemove} onStatusClick={onStatusClick} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.pagination.totalItems > 0 && (
          <TablePagination
            component="div"
            count={data.pagination.totalItems}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        )}
      </Paper>

      <EnquiryStatusMenu
        anchorEl={statusMenu?.anchor ?? null}
        current={statusMenu?.enquiry.status}
        onClose={() => setStatusMenu(null)}
        onPick={pickStatus}
      />

      {editing !== undefined && (
        <EnquiryDialog
          open
          enquiry={editing}
          classOptions={classOptions}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}

      <ConfirmationDialog
        open={!!removing}
        onClose={() => { if (!removeBusy) setRemoving(null); }}
        onConfirm={confirmRemove}
        title="Remove this enquiry?"
        message={removing ? `${removing.childName} (${removing.parentName}) will be taken off the list. Use “Not interested” instead if the family decided not to apply.` : ''}
        confirmText="Remove"
        loading={removeBusy}
      />
    </Box>
  );
};

export default AdmissionEnquiriesPage;
